/**
 * Broker Authentication Repository
 * Handles all authentication-related API calls
 */

import axios, { AxiosInstance } from 'axios'
import API_CONFIG from '../config/api.config'

interface LoginRequest {
  username: string
  password: string
}

/**
 * Body of the login endpoints. Two shapes, one type (mirrors the backend's
 * LoginResponseVM): a finished login carries `id_token`; an account with a
 * confirmed second factor gets `{ mfaRequired: true, mfaToken, expiresIn }`
 * instead and must call `verifyTwoFactor` (SFB-1164).
 */
interface LoginResponse {
  id_token?: string
  mfaRequired?: boolean
  mfaToken?: string
  expiresIn?: number
}

export interface BrokerSession {
  token: string
  partnerId: number
  user: {
    username: string
    roles: string[]
    partnerId: number
    partnerStaffRole?: string
  }
}

export interface MfaChallengeResult {
  mfaRequired: true
  mfaToken: string
  expiresIn: number
}

export type LoginResult = BrokerSession | MfaChallengeResult

export function isMfaChallenge(result: LoginResult): result is MfaChallengeResult {
  return (result as MfaChallengeResult).mfaRequired === true
}

interface JWTPayload {
  sub: string
  auth: string
  iat: number
  exp: number
  pid: number // Partner ID
  psRole?: string // Partner Staff Role (ADMIN, MANAGER, BUSINESS_DEVELOPER, OPERATIONS)
}

export interface UserAccount {
  id: number
  login: string
  firstName: string
  lastName: string
  email: string
  imageUrl: string
  activated: boolean
  langKey: string
  authorities: string[]
  partnerStaffRole?: string
  /** SFB-1164: mirrors GET /api/account/2fa `enabled` for callers that only need the flag. */
  twoFactorEnabled?: boolean
}

class AuthRepository {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        // Handle common auth errors
        if (error.response) {
          switch (error.response.status) {
            case 401:
              // Unauthorized - clear token and redirect to login
              this.clearAuthData()
              break
            case 403:
              if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.error('Broker API access forbidden:', error.response.status)
              }
              break
            case 500:
              if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.error('Broker API server error:', error.response.status)
              }
              break
          }
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Login with username and password.
   *
   * Resolves to a session, or to an MFA challenge when the account has a
   * second factor — check with `isMfaChallenge` and finish via
   * `verifyTwoFactor`. No token is stored on the challenge path.
   */
  async login(username: string, password: string): Promise<LoginResult> {
    try {
      const response = await this.api.post<LoginResponse>(
        API_CONFIG.AUTH.LOGIN,
        { username, password } as LoginRequest
      )
      return this.acceptLoginResponse(response.data)
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Broker login failed:', error?.message || error)
      }
      throw error
    }
  }

  /**
   * Second login step (SFB-1164): trades the challenge token plus a TOTP or
   * backup code for the session. The response is identical to a one-step
   * login, so the same handling applies.
   * Errors: 401 with message `error.2fa.invalidCode` | `error.2fa.invalidChallenge`
   * | `error.2fa.challengeExpired`; 429 `error.2fa.locked`.
   */
  async verifyTwoFactor(mfaToken: string, code: string): Promise<BrokerSession> {
    const response = await this.api.post<LoginResponse>(
      API_CONFIG.TWO_FACTOR.VERIFY_LOGIN,
      { mfaToken, code }
    )
    const result = this.acceptLoginResponse(response.data)
    if (isMfaChallenge(result)) {
      // The verify endpoint never issues a new challenge; treat it as a broken response.
      throw new Error('Unexpected MFA challenge after verification')
    }
    return result
  }

  /**
   * Branches on the login body: challenge → returned untouched (nothing
   * stored); token → parsed, persisted, and turned into a session.
   */
  private acceptLoginResponse(data: LoginResponse): LoginResult {
    if (data?.mfaRequired) {
      if (!data.mfaToken) {
        throw new Error('MFA challenge without a challenge token')
      }
      return { mfaRequired: true, mfaToken: data.mfaToken, expiresIn: data.expiresIn || 300 }
    }

    const token = data?.id_token
    if (!token) {
      throw new Error('No token received from server')
    }

    // Parse JWT to extract user info and PID
    const payload = this.parseJWT(token)

    // Store auth data
    this.storeAuthData(token, payload)

    return {
      token,
      partnerId: payload.pid,
      user: {
        username: payload.sub,
        roles: payload.auth.split(','),
        partnerId: payload.pid,
        partnerStaffRole: payload.psRole || undefined
      }
    }
  }

  /**
   * Parse JWT token to extract payload
   */
  parseJWT(token: string): JWTPayload {
    try {
      const base64Payload = token.split('.')[1]
      // Decode base64 (browser-compatible)
      const base64 = base64Payload.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Failed to parse JWT:', error?.message || error)
      }
      throw new Error('Invalid token format')
    }
  }

  /**
   * Store authentication data in localStorage
   */
  private storeAuthData(token: string, payload: JWTPayload): void {
    localStorage.setItem('broker_token', token)
    localStorage.setItem('broker_pid', payload.pid.toString())
    localStorage.setItem('broker_user', payload.sub)
    localStorage.setItem('broker_token_exp', payload.exp.toString())
  }

  /**
   * Clear authentication data from localStorage
   */
  clearAuthData(): void {
    localStorage.removeItem('broker_token')
    localStorage.removeItem('broker_pid')
    localStorage.removeItem('broker_user')
    localStorage.removeItem('broker_token_exp')
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken()
    if (!token) return false

    // Check token expiration
    const expiration = localStorage.getItem('broker_token_exp')
    if (expiration) {
      const expTime = parseInt(expiration) * 1000 // Convert to milliseconds
      if (Date.now() > expTime) {
        this.clearAuthData()
        return false
      }
    }

    return true
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem('broker_token')
  }

  /**
   * Get stored partner ID
   */
  getPartnerId(): number | null {
    const pid = localStorage.getItem('broker_pid')
    return pid ? parseInt(pid) : null
  }

  /**
   * Get authorization header
   */
  getAuthHeader(): { Authorization: string } | {} {
    const token = this.getToken()
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  /**
   * Get current user account information including profile picture
   */
  async getUser(): Promise<UserAccount> {
    try {
      const token = this.getToken()

      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await this.api.get<UserAccount>(
        `${API_CONFIG.BASE_URL}/api/account`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      return response.data
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Failed to get user account:', error?.message || error)
      }
      throw error
    }
  }

  /**
   * Update the current user's account (JHipster POST /api/account).
   * Send the full UserDTO fetched from getUser() with the edited fields.
   */
  async updateAccount(account: UserAccount): Promise<void> {
    const token = this.getToken()
    if (!token) {
      throw new Error('No authentication token found')
    }
    await this.api.post(
      `${API_CONFIG.BASE_URL}/api/account`,
      account,
      { headers: { Authorization: `Bearer ${token}` } }
    )
  }

  /**
   * Change the current user's password (POST /api/account/change-password).
   * The backend responds 400 when the current password is wrong or the new
   * one fails the length rules.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = this.getToken()
    if (!token) {
      throw new Error('No authentication token found')
    }
    await this.api.post(
      `${API_CONFIG.BASE_URL}/api/account/change-password`,
      { currentPassword, newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    )
  }

  /**
   * Initialize reset password flow
   */
  async initResetPassword(mail: string): Promise<void> {
    try {
      await this.api.post(API_CONFIG.AUTH.RESET_PASSWORD_INIT, {
        mail,
        platform: 'BROKERADMIN'
      })
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize reset password:', error?.message || error)
      }
      throw error
    }
  }

  /**
   * Finish reset password flow
   */
  async finishPasswordReset(payload: { key: string; newPassword: string }): Promise<void> {
    try {
      await this.api.post(API_CONFIG.AUTH.RESET_PASSWORD_FINISH, payload)
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Failed to finish reset password:', error?.message || error)
      }
      throw error
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    // Could call logout endpoint here if needed
    this.clearAuthData()
  }
}

export default new AuthRepository()